import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiArrowRight, mdiTune } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import type { TicketSubtype } from '../../api/schema'

/** The aggregate a detector copies onto the ticket: the norm it was judged
 * against (spec 2026-07-17-01). Present on threshold-based subtypes only. */
type Aggregate = {
    op?: string
    value?: number
    window_months?: number
}

const OP_SYMBOL: Record<string, string> = { lte: '≤', lt: '<', gte: '≥', gt: '>', eq: '=' }

type Props = {
    orgCode: string
    subtype: TicketSubtype
    /** The issue's raw properties — carries the measured aggregate/norm. */
    properties?: Record<string, unknown>
}

/**
 * Box 2 of the ticket triptych: the rule the ticket was evaluated against.
 * Read-only here — the threshold lives on a compliance_rule, edited on the
 * dedicated "Règles de gestion" page (spec 2026-07-18-01). The card links
 * there, deep-anchored to the rule itself (#rule-<code>) when the ticket names
 * it, else to the rule's control group (#control-<code>).
 */
export function TicketRuleCard({ orgCode, subtype, properties }: Props) {
    const { t } = useTranslation('issues')
    const control = subtype.rule_binding?.control
    if (!control) return null

    // The norm the ticket was judged against, when the detector copied an
    // aggregate onto the issue. Absent for expiry/cascade subtypes — those
    // still show the rule family and the link.
    const aggregate = (properties?.aggregate as Aggregate | undefined) ?? undefined
    const thresholdText =
        aggregate && typeof aggregate.value === 'number'
            ? `${aggregate.op ? (OP_SYMBOL[aggregate.op] ?? aggregate.op) + ' ' : ''}${aggregate.value}${
                  aggregate.window_months
                      ? ` / ${aggregate.window_months} ${t('rule.months', { defaultValue: 'mois' })}`
                      : ''
              }`.trim()
            : null

    const controlLabel = t(`rule.control.${control}`, { defaultValue: control })
    // The rule that raised the ticket, when the detector stamped it (a family
    // member scoped to a building or a type included): the link opens on that
    // rule, inside its control group (spec 2026-10-27-21 § 3).
    const ruleCode = [properties?.rule_code, properties?.suggestion_code, properties?.compliance_rule_code]
        .find((v): v is string => typeof v === 'string' && v !== '')

    return (
        <Card data-testid="ticket-rule-card">
            <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                    <AppIcon path={mdiTune} size="sm" />
                    {t('rule.card_title', { defaultValue: 'Règle' })}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm text-muted-foreground">{controlLabel}</span>
                    {thresholdText && (
                        <span
                            className="text-sm font-medium text-foreground tabular-nums"
                            data-testid="ticket-rule-threshold"
                        >
                            {thresholdText}
                        </span>
                    )}
                </div>
                <Link
                    to="/$org/business-rules"
                    params={{ org: orgCode }}
                    hash={ruleCode ? `rule-${ruleCode}` : `control-${control}`}
                    className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                    data-testid="ticket-rule-link"
                >
                    {t('rule.adjust_link', { defaultValue: 'Ajuster dans les Règles de gestion' })}
                    <AppIcon path={mdiArrowRight} size="xs" />
                </Link>
            </CardContent>
        </Card>
    )
}
